import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { IRefRegisterPersonalData, IRegisterPersonalData } from 'entities';
import { cnpj, cpf } from 'cpf-cnpj-validator';
import { DateField, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import ErrorIcon from '@mui/icons-material/Error';
import { useApp, useAuth, useInformationBase, useRegisterForm } from '../../context';
import { TypeUser } from '../../types/enums';
import { MASK_CNPJ, MASK_CPF } from '../../constants';
import { validateEmail } from '../../utils/validateEmail';
import { Input } from '../Inputs/Input';
import { Container, Content } from './styles';
import { toCnpjMask } from '../../utils/toCnpjMask';
import { toCpfMask } from '../../utils/toCpfMask';
import { UserController } from '../../controller/UserController';
import { unMask } from '../../utils/removeMask';
import InformationBaseLegalPersonController from '../../controller/InformationBaseLegalPersonController';
import InformationBasePrivateIndividualController from '../../controller/InformationBasePrivateIndividualController';
import { useAlert } from '../../hooks/useAlert';

export const RegisterPersonalData = forwardRef<IRefRegisterPersonalData>((_, ref) => {
  const refComponent = useRef<HTMLDivElement>(null);
  const { setLoading } = useApp();
  const alert = useAlert();
  const { user } = useAuth();
  const [searchedDocument, setSearchedDocument] = useState('');
  const [personFound, setPersonFound] = useState(true);
  const { dataInformationBaseLegalPerson, setDataInformationBaseLegalPerson } =
    useInformationBase();
  const {
    firstName,
    lastName,
    email,
    document,
    formSubmited,
    userType,
    setFirstName,
    setLastName,
    setUserType,
  } = useRegisterForm();
  const [defaultValues] = useState<{
    document: string;
    firstName: string;
    lastName: string;
    corporateName: string;
    email: string;
    birthDate: Date | null;
  }>({
    document,
    firstName,
    lastName,
    corporateName: '',
    email,
    birthDate: null,
  });

  const {
    control,
    setError,
    setValue,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm({
    reValidateMode: 'onChange',
    defaultValues,
  });

  const data = watch();

  const checkHasError = useCallback(async () => {
    let hasError = false;
    const documentUnmasked = unMask(data.document);
    const isValidDocument =
      documentUnmasked.length === 11
        ? cpf.isValid(documentUnmasked)
        : cnpj.isValid(documentUnmasked);
    if (!documentUnmasked) {
      setError('document', { type: 'manual', message: 'Campo obrigatório' });
      hasError = true;
    } else if (!isValidDocument) {
      clearErrors('document');
      setError('document', { type: 'manual', message: 'Documento inválido' });
      hasError = true;
    } else if (isValidDocument && documentUnmasked !== document) {
      clearErrors('document');
      const alreadyExists = await UserController.documentAlreadyExists(unMask(data.document));
      if (alreadyExists) {
        setError('document', { type: 'manual', message: 'Documento já cadastrado' });
        hasError = true;
      }
    } else {
      clearErrors('document');
    }

    if (unMask(data.document).length === 11 && user.type === TypeUser.BASEUSER) {
      if (!data.birthDate) {
        setError('birthDate', { type: 'manual', message: 'Campo obrigatório' });
        hasError = true;
      } else if (
        data.birthDate >
        new Date(new Date().getUTCFullYear() - 18, new Date().getMonth(), new Date().getDate())
      ) {
        setError('birthDate', { type: 'manual', message: 'Idade deve ser maior que 18' });
        hasError = true;
      } else if (!isValidBirthDate(data.birthDate)) {
        setError('birthDate', { type: 'manual', message: 'Data inválida' });
        hasError = true;
      } else {
        clearErrors('birthDate');
      }
    }

    if (documentUnmasked.length >= 14) {
      clearErrors('firstName');
      clearErrors('lastName');
      if (!data.corporateName) {
        setError('corporateName', { type: 'manual', message: 'Campo obrigatório' });
        hasError = true;
      } else {
        clearErrors('corporateName');
      }
    }

    if (documentUnmasked.length <= 11) {
      clearErrors('corporateName');
      if (!data.firstName) {
        setError('firstName', { type: 'manual', message: 'Campo obrigatório' });
        hasError = true;
      } else {
        clearErrors('firstName');
      }

      if (!data.lastName) {
        setError('lastName', { type: 'manual', message: 'Campo obrigatório' });
        hasError = true;
      } else {
        clearErrors('lastName');
      }
    }

    if (!data.email) {
      setError('email', { type: 'manual', message: 'Campo obrigatório' });
      hasError = true;
    } else if (!validateEmail(data.email)) {
      setError('email', { type: 'manual', message: 'E-mail inválido' });
      hasError = true;
    } else if (validateEmail(data.email) && data.email !== email) {
      const alreadyExists = await UserController.emailAlreadyExists(data.email);
      if (alreadyExists) {
        setError('email', { type: 'manual', message: 'E-mail já cadastrado' });
        hasError = true;
      }
    } else {
      clearErrors('email');
    }
    return hasError;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    clearErrors,
    data.birthDate,
    data.corporateName,
    data.document,
    data.email,
    data.firstName,
    data.lastName,
    document,
    email,
    setError,
  ]);

  const handleGetDataPerson = async (documentInput: string, birthDate?: Date) => {
    const documentUnmasked = unMask(documentInput);
    if (documentUnmasked?.length === 14) {
      const isValidDocument = cnpj.isValid(documentUnmasked);
      if (isValidDocument) {
        if (dataInformationBaseLegalPerson?.document === documentUnmasked) return;
        setLoading(true);
        await InformationBaseLegalPersonController.getLegalPerson(documentUnmasked)
          .then((response) => {
            if (!response || response.corporateName === '') {
              setPersonFound(false);
              setSearchedDocument(data.document);
              alert.show({ message: 'Não foi possível localizar os dados', type: 'error' });
              return;
            }
            setPersonFound(true);
            setValue('corporateName', response.corporateName);
            setDataInformationBaseLegalPerson(response);
          })
          .finally(() => {
            setUserType(TypeUser.COMPANY);
            setLoading(false);
          });
      }
    } else {
      if (!birthDate) return;
      const isValidDocument = cpf.isValid(documentUnmasked);
      if (isValidDocument) {
        setLoading(true);
        await InformationBasePrivateIndividualController.getPrivateIndividual(
          documentUnmasked,
          birthDate,
        )
          .then((response) => {
            if (!response || response.name === '') {
              setPersonFound(false);
              alert.show({ message: 'Não foi possível localizar os dados', type: 'error' });
              return;
            }
            if (response.codeStatus === 'P009M002') {
              setPersonFound(false);
              alert.show({
                message: 'A data de nascimento inserida não corresponde ao documento',
                type: 'error',
              });
              setError('birthDate', {
                type: 'manual',
                message: 'Data informada não corresponde ao documento',
              });
              return;
            }
            setPersonFound(true);
            setSearchedDocument(data.document);
            const fullName = response.name;
            const [responsefirstName, ...rest] = fullName.split(' ');
            const responselastName = rest.join(' ');
            setFirstName(responsefirstName);
            setLastName(responselastName);
            setValue('firstName', responsefirstName);
            setValue('lastName', responselastName);
          })
          .finally(() => {
            setLoading(false);
            setUserType(TypeUser.USER);
          });
      }
    }
  };

  const handleUpdatePersonalData = async () => {
    const newData: IRegisterPersonalData = {
      document: unMask(data.document),
      firstName: unMask(data.document).length === 11 ? data.firstName : data.corporateName,
      lastName: data.lastName,
      email: data.email,
      hasError: await checkHasError(),
      birthDate: data.birthDate,
    };
    return newData;
  };

  const handleFocusOnError = () => {
    refComponent.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useImperativeHandle(ref, () => ({
    handleUpdatePersonalData,
    handleFocusOnError,
  }));

  useEffect(() => {
    if (document)
      setValue('document', document.length >= 14 ? toCnpjMask(document) : toCpfMask(document));
    if (unMask(document).length >= 14) setValue('corporateName', firstName);
    else setValue('firstName', firstName);
    setValue('lastName', lastName);
    setValue('email', email);
  }, [document, email, firstName, lastName, setValue]);

  useEffect(() => {
    checkHasError();
  }, [checkHasError]);

  const isValidBirthDate = (birthDate: Date) => {
    const date = typeof birthDate === 'string' ? new Date(birthDate) : birthDate;

    if (isNaN(date.getTime())) {
      return false;
    }

    return true;
  };

  const handleSearchPerson = () => {
    if (cpf.isValid(data.document) && data.birthDate) {
      const birthDate = new Date(data.birthDate);
      if (!isValidBirthDate(birthDate)) return;
      if (isValidBirthDate(birthDate) && searchedDocument !== data.document) {
        handleGetDataPerson(data.document, birthDate);
      }
    } else if (cnpj.isValid(data.document)) {
      if (searchedDocument !== data.document) {
        handleGetDataPerson(data.document);
      }
    }
  };

  return (
    <Container ref={refComponent}>
      <Content hasErrorOnBirthDate={formSubmited && !!errors.birthDate?.message}>
        <div className="contentDocument">
          <div className="contentData">
            {user.type !== TypeUser.BASEUSER && document ? (
              <div id="document-input" className="contentDocument">
                <p>{unMask(document).length === 11 ? 'CPF' : 'CNPJ'}</p>
                <p className="document">
                  {unMask(document).length === 11 ? toCpfMask(document) : toCnpjMask(document)}
                </p>
              </div>
            ) : (
              <div className="contentData">
                <div className="contentDocumentBirthDate">
                  <div id="document-input">
                    {userType === TypeUser.USER ? (
                      <p className="documentLabel">CPF </p>
                    ) : (
                      <p className="documentLabel">CPF ou CNPJ</p>
                    )}
                    <Input
                      mask={
                        user.type === TypeUser.USER
                          ? MASK_CPF
                          : data.document.length > 14
                          ? MASK_CNPJ
                          : MASK_CPF
                      }
                      name="document"
                      control={control}
                      type="text"
                      variant="standard"
                      fullWidth
                      onChange={(e) => {
                        setValue('birthDate', null);
                      }}
                      onBlur={handleSearchPerson}
                      width="15rem"
                      disabled={!!user.document}
                      lengthInput={user.type === TypeUser.USER ? 14 : 18}
                      error={formSubmited ? !!errors.document : false}
                      errorText={formSubmited ? errors.document?.message : ''}
                    />
                  </div>
                  {cpf.isValid(data.document) && !user.document && (
                    <div className="containerBirthDate">
                      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                        <Controller
                          control={control}
                          name="birthDate"
                          render={({ field: { onChange, value } }) => (
                            <DateField
                              value={value}
                              onChange={(e) => onChange(e)}
                              onBlur={handleSearchPerson}
                              minDate={new Date(1900, 0, 1)}
                              label="Data de Nascimento"
                              maxDate={
                                new Date(
                                  new Date().getUTCFullYear() - 18,
                                  new Date().getMonth(),
                                  new Date().getDate(),
                                )
                              }
                            />
                          )}
                        />
                      </LocalizationProvider>
                      {formSubmited && errors?.birthDate && (
                        <span className="fieldError">
                          <ErrorIcon fontSize="inherit" /> {errors.birthDate?.message}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="containerDataUser">
          {data.document?.length <= 14 ? (
            <>
              <div className="contentData" id="name-input">
                <p>Nome</p>
                <div>
                  <Input
                    name="firstName"
                    control={control}
                    variant="standard"
                    lengthInput={30}
                    error={formSubmited ? !!errors.firstName : false}
                    errorText={formSubmited ? errors.firstName?.message : ''}
                    fullWidth
                    disabled={personFound}
                  />
                </div>
              </div>
              <div className="contentData">
                <p>Sobrenome</p>
                <div>
                  <Input
                    name="lastName"
                    control={control}
                    variant="standard"
                    lengthInput={30}
                    error={formSubmited ? !!errors.lastName : false}
                    errorText={formSubmited ? errors.lastName?.message : ''}
                    fullWidth
                    disabled={personFound}
                  />
                </div>
                <div />
              </div>
            </>
          ) : (
            <div className="contentData" id="name-input">
              <p>Razão Social</p>
              <div>
                <Input
                  name="corporateName"
                  control={control}
                  variant="standard"
                  lengthInput={40}
                  error={formSubmited ? !!errors.firstName : false}
                  errorText={formSubmited ? errors.firstName?.message : ''}
                  fullWidth
                  disabled={personFound}
                />
              </div>
            </div>
          )}
          <div className="contentData">
            <p>E-mail</p>
            <div>
              <Input
                name="email"
                control={control}
                variant="standard"
                lengthInput={50}
                error={formSubmited ? !!errors.email : false}
                errorText={formSubmited ? errors.email?.message : ''}
                fullWidth
                disabled
              />
            </div>
          </div>
        </div>
      </Content>
    </Container>
  );
});
